import React from "react";
import { createContext } from "react";
import PropTypes from "prop-types";
import { client } from "../services/axiosClient";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setAuthTokens,
  setUser,
  setUserDetails,
} from "../reducers/authReducer";
import { baseUrl } from "utils/backendConfig";
import { useTokenSync } from "../hooks/useTokenSync";

const AuthContext = createContext();

export default AuthContext;

const getErrorMessage = (error) => {
  if (!error.response) {
    return navigator.onLine
      ? "Unable to connect to server. Please check your connection."
      : "No internet connection detected.";
  }

  const { status, data } = error.response;

  switch (status) {
    case 401:
      return data.detail || "Invalid e-mail or password";
    case 429:
      return (
        data.detail ||
        "Too many login attempts. Please try again in 15 minutes."
      );
    default:
      return "An unexpected error occurred. Please contact support.";
  }
};

export const AuthProvider = ({ children }) => {
  const userDetails = useSelector((state) => state.auth.userDetails);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use the token sync hook for better cross-window synchronization
  const { authTokens, user } = useTokenSync();

  const detectProjectMismatch = (accessToken, currentUserDetails) => {
    if (!accessToken || !currentUserDetails) return false;

    try {
      const tokenData = jwtDecode(accessToken);
      const tokenImplementation = tokenData.implementation_code;
      const localImplementation = currentUserDetails.implementation_code;

      return (
        tokenImplementation &&
        localImplementation &&
        tokenImplementation !== localImplementation
      );
    } catch (error) {
      console.error("Error detecting project mismatch:", error);
      return false;
    }
  };

  const loginUser = async (e, setLoginError, setIsLoggedIn, redir) => {
    e.preventDefault();

    const credentials = {
      email: e.target.email.value,
      password: e.target.password.value,
    };

    try {
      const response = await client.post(baseUrl + "token/", credentials, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response?.data) {
        setLoginError("Invalid response from server");
        return;
      }

      const { data } = response;

      dispatch(setAuthTokens(data));
      dispatch(setUser(jwtDecode(data.access)));
      setIsLoggedIn(true);

      const hasMismatch = detectProjectMismatch(data.access, userDetails);

      if (redir && userDetails && !hasMismatch) {
        navigate(redir);
        return;
      }

      dispatch(setUserDetails(null));
      setTimeout(() => navigate("/welcome"), 600);
    } catch (error) {
      console.error("Login Error: ", error);
      setLoginError(getErrorMessage(error));
    }
  };

  let logoutUser = (e, error) => {
    if (e) e.preventDefault();
    dispatch(setAuthTokens(null));
    dispatch(setUser(null));
    dispatch(setUserDetails(null));
    navigate("/auth/login", { state: error ? { error } : {} });
  };

  let contextData = {
    user: user,
    userDetails: userDetails,
    authTokens: authTokens,
    loginUser: loginUser,
    logoutUser: logoutUser,
  };

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.element.isRequired,
};
